import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import Avatar from '../components/Avatar';
import ProfilePhotoUploader from '../components/ProfilePhotoUploader';

const Profile = () => {
  const { user, updateUser } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({});
  const [userGroups, setUserGroups] = useState([]);
  const [userActivity, setUserActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const [editMode, setEditMode] = useState(false);
  const [profileData, setProfileData] = useState({
    business_name: '',
    business_description: '',
    industry: '',
    founded_year: '',
    employee_count: '',
    website: '',
    business_email: '',
    business_phone: '',
    address: '',
    specialties: [],
    certifications: []
  });
  
  // Profile photo state
  const [profilePhoto, setProfilePhoto] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  // Modal body scroll lock effect
  useEffect(() => {
    if (editMode) {
      // Lock body scroll and hide potential bleeding elements
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
      
      // Add styles to suppress high z-index elements
      const style = document.createElement('style');
      style.id = 'modal-suppression-styles';
      style.textContent = `
        .modal-open .border-teal-500,
        .modal-open nav,
        .modal-open [class*="border-b-2"],
        .modal-open [class*="z-"] {
          z-index: 1 !important;
        }
        .modal-open .profile-tab-nav {
          opacity: 0.3;
          pointer-events: none;
        }
      `;
      document.head.appendChild(style);
    } else {
      // Restore body scroll and remove suppression
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
      
      const suppressionStyles = document.getElementById('modal-suppression-styles');
      if (suppressionStyles) {
        suppressionStyles.remove();
      }
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
      const suppressionStyles = document.getElementById('modal-suppression-styles');
      if (suppressionStyles) {
        suppressionStyles.remove();
      }
    };
  }, [editMode]);

  const fetchProfile = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/profile`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setProfile(response.data.profile);
      setStats(response.data.stats);
      setUserGroups(response.data.user_groups);
      setUserActivity(response.data.user_activity);
      
      if (response.data.profile) {
        setProfileData({
          business_name: response.data.profile.business_name || '',
          business_description: response.data.profile.business_description || '',
          industry: response.data.profile.industry || '',
          founded_year: response.data.profile.founded_year || '',
          employee_count: response.data.profile.employee_count || '',
          website: response.data.profile.website || '',
          business_email: response.data.profile.business_email || '',
          business_phone: response.data.profile.business_phone || '',
          address: response.data.profile.address || '',
          specialties: response.data.profile.specialties || [],
          certifications: response.data.profile.certifications || []
        });
        
        // Set profile photo from profile data OR user data
        const profileImage = response.data.profile.profile_image || user?.profile_image;
        setProfilePhoto(profileImage);
        
        // If we got a profile image from the backend that's different from user context, update user context
        if (profileImage && profileImage !== user?.profile_image) {
          updateUser({ profile_image: profileImage });
        }
      } else {
        // If no profile data, check if user has profile image
        setProfilePhoto(user?.profile_image);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Prepare data with correct types
      const updateData = {
        ...profileData,
        founded_year: profileData.founded_year ? parseInt(profileData.founded_year) : null,
        specialties: profileData.specialties || [],
        certifications: profileData.certifications || []
      };
      
      const response = await axios.post(`${backendUrl}/api/profile`, updateData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setProfile(response.data);
      setEditMode(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const addSpecialty = () => {
    setProfileData({
      ...profileData,
      specialties: [...profileData.specialties, '']
    });
  };

  const updateSpecialty = (index, value) => {
    const updatedSpecialties = [...profileData.specialties];
    updatedSpecialties[index] = value;
    setProfileData({
      ...profileData,
      specialties: updatedSpecialties
    });
  };

  const removeSpecialty = (index) => {
    setProfileData({
      ...profileData,
      specialties: profileData.specialties.filter((_, i) => i !== index)
    });
  };

  const handlePhotoUpdate = (newPhotoUrl) => {
    console.log('🔄 STATE SYNC DEBUG - Profile photo update started');
    console.log('📸 New photo URL received:', newPhotoUrl);
    console.log('👤 Current user in AuthContext:', user);
    console.log('🔗 UpdateUser function available:', !!updateUser);
    
    // Update local profile data
    console.log('💾 Updating local profile data...');
    const oldProfileData = {...profileData};
    setProfileData(prev => {
      const updated = {
        ...prev,
        profile_image: newPhotoUrl
      };
      console.log('💾 Local profile data updated:', {
        old: prev.profile_image,
        new: updated.profile_image
      });
      return updated;
    });
    
    // Update the profile photo state
    setProfilePhoto(newPhotoUrl);
    
    // Update the local profile state  
    setProfile(prevProfile => ({ 
      ...prevProfile, 
      profile_image: newPhotoUrl 
    }));
    
    // Update global user state in AuthContext
    if (updateUser) {
      console.log('🌐 Updating global user state in AuthContext...');
      const updateData = { profile_image: newPhotoUrl };
      console.log('🌐 Update data being sent:', updateData);
      
      updateUser(updateData);
      
      console.log('✅ Global user state update completed');
      console.log('✅ New profile photo URL set globally:', newPhotoUrl);
      
      // Trigger a small delay and log the user state again to verify
      setTimeout(() => {
        console.log('🔍 STATE SYNC VERIFICATION - User state after update:', user);
        console.log('🔍 User profile_image after update:', user?.profile_image);
      }, 100);
    } else {
      console.error('❌ updateUser function not available in AuthContext');
    }
  };

  const addCertification = () => {
    setProfileData({
      ...profileData,
      certifications: [...profileData.certifications, '']
    });
  };

  const updateCertification = (index, value) => {
    const updatedCertifications = [...profileData.certifications];
    updatedCertifications[index] = value;
    setProfileData({
      ...profileData,
      certifications: updatedCertifications
    });
  };

  const removeCertification = (index) => {
    setProfileData({
      ...profileData,
      certifications: profileData.certifications.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="flex items-center space-x-6">
              <div className="w-24 h-24 bg-gray-300 rounded-full"></div>
              <div className="flex-1">
                <div className="h-8 bg-gray-300 rounded w-1/3 mb-4"></div>
                <div className="h-4 bg-gray-300 rounded w-2/3"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Profile Header */}
      <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start space-x-6 mb-6 lg:mb-0">
            {/* Profile Photo */}
            <div className="relative">
              <Avatar 
                user={user} 
                size="xlarge" 
              />
            </div>

            {/* Basic Info */}
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  {profile?.business_name || user?.company_name || 'Your Business Name'}
                </h1>
                {user?.is_verified && (
                  <span className="text-blue-500 text-xl">✓</span>
                )}
              </div>
              
              <p className="text-gray-600 mb-4">
                {profile?.business_description || "No business description provided. Please update your profile with business information."}
              </p>

              {/* Business Details */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <span>🏢</span>
                  <span>{profile?.industry || 'Not specified'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span>📍</span>
                  <span>{user?.location || 'Location not provided'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span>📅</span>
                  <span>{profile?.founded_year ? `Founded ${profile.founded_year}` : 'Founded year not provided'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span>👥</span>
                  <span>{profile?.employee_count || 'Employee count not specified'}</span>
                </div>
                {profile?.website && (
                  <div className="flex items-center space-x-2">
                    <span>🌐</span>
                    <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">
                      {profile.website.replace('https://', '').replace('http://', '')}
                    </a>
                  </div>
                )}
                {profile?.business_email && (
                  <div className="flex items-center space-x-2">
                    <span>✉️</span>
                    <span>{profile.business_email}</span>
                  </div>
                )}
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-4">
                {profile?.specialties && profile.specialties.length > 0 ? (
                  profile.specialties.map((specialty, index) => (
                    <span key={index} className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm font-medium">
                      #{specialty}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500 text-sm">No specialties listed</span>
                )}
              </div>
            </div>
          </div>

          {/* Edit Profile Button */}
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setEditMode(!editMode)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <span>✏️</span>
              <span>Edit Profile</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-8 mt-8 pt-8 border-t border-gray-200">
          <div className="text-center">
            <div className="text-3xl font-bold text-teal-600">{stats.posts_count || 0}</div>
            <div className="text-gray-600 text-sm">Posts</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-teal-600">{stats.followers_count || 0}</div>
            <div className="text-gray-600 text-sm">Followers</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-teal-600">{stats.following_count || 0}</div>
            <div className="text-gray-600 text-sm">Following</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-teal-600">{stats.groups_count || 0}</div>
            <div className="text-gray-600 text-sm">Groups</div>
          </div>
        </div>
      </div>

      {/* Content Tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className={`flex space-x-8 px-6 profile-tab-nav ${editMode ? 'opacity-30 pointer-events-none' : ''}`}>
            {[
              { id: 'posts', label: 'My Posts', count: stats.posts_count },
              { id: 'activity', label: 'Activity' },
              { id: 'groups', label: 'Groups', count: stats.groups_count }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm relative ${
                  activeTab === tab.id
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                style={{ zIndex: editMode ? '1' : 'auto' }}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-gray-100 text-gray-600">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'posts' && (
            <div className="space-y-6">
              {userActivity.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No activity yet</p>
                  <p className="text-gray-400 text-sm">Your recent activity will appear here</p>
                </div>
              ) : (
                userActivity.map((post) => (
                  <div key={post.id} className="border-b border-gray-100 pb-6 last:border-b-0">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-teal-500 text-white rounded-full flex items-center justify-center font-medium">
                        {user?.full_name?.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-medium text-gray-900">{user?.full_name}</span>
                          <span className="text-gray-500 text-sm">{formatTimeAgo(post.created_at)}</span>
                        </div>
                        <p className="text-gray-900 mb-3">{post.content}</p>
                        {post.hashtags && post.hashtags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {post.hashtags.map((tag, index) => (
                              <span key={index} className="text-teal-600 text-sm">#{tag}</span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>❤️ {post.likes_count}</span>
                          <span>💬 {post.comments_count}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-6">
              <div className="text-center py-12">
                <p className="text-gray-500">Activity feed coming soon</p>
                <p className="text-gray-400 text-sm">Track your interactions and engagement here</p>
              </div>
            </div>
          )}

          {activeTab === 'groups' && (
            <div className="space-y-6">
              {userGroups.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No groups joined yet</p>
                  <p className="text-gray-400 text-sm">Join groups to connect with other merchants in your industry</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {userGroups.map((group) => (
                    <div key={group.id} className="bg-white border rounded-lg p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-lg flex items-center justify-center font-bold text-xl">
                            {group.name.substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{group.name}</h3>
                            <p className="text-sm text-gray-600">{group.member_count} members</p>
                          </div>
                        </div>
                        <span className="bg-teal-100 text-teal-800 px-2 py-1 rounded-full text-xs font-medium">
                          {group.user_role}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 text-sm mb-4">{group.description}</p>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>📍 {group.location}</span>
                        <span>{group.posts_count} posts</span>
                      </div>
                      
                      {group.industry_tags && group.industry_tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {group.industry_tags.slice(0, 3).map((tag, index) => (
                            <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Profile Modal */}
      {editMode && (
        <>
          {/* Portal-style backdrop */}
          <div 
            className="fixed inset-0 bg-black transition-opacity duration-300 z-[9998]"
            style={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)'
            }}
            onClick={() => setEditMode(false)}
          />
          
          {/* Modal Content */}
          <div 
            className="fixed inset-0 flex items-center justify-center p-4 z-[9999]"
            style={{ 
              transform: 'translateZ(0)',
              isolation: 'isolate'
            }}
          >
            <div 
              className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative"
              style={{ 
                transform: 'translateZ(0)',
                isolation: 'isolate',
                zIndex: '10000'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Scrollable Content */}
              <div className="overflow-y-auto max-h-[90vh]">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6 sticky top-0 bg-white border-b border-gray-200 pb-4 -mt-6 -mx-6 px-6 pt-6 z-[10001]">
                    <h2 className="text-2xl font-bold text-gray-900">Edit Profile</h2>
                    <button
                      onClick={() => setEditMode(false)}
                      className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Profile Photo Section */}
                  <div className="mb-8 p-6 bg-gray-50 rounded-lg">
                    <ProfilePhotoUploader 
                      user={user} 
                      onPhotoUpdate={handlePhotoUpdate}
                    />
                  </div>

              <div className="space-y-6 mt-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      value={profileData.business_name}
                      onChange={(e) => setProfileData({...profileData, business_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500 bg-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Industry *
                    </label>
                    <select
                      value={profileData.industry}
                      onChange={(e) => setProfileData({...profileData, industry: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500 bg-white"
                    >
                      <option value="">Select industry</option>
                      <option value="Textile & Fashion">Textile & Fashion</option>
                      <option value="Electronics">Electronics</option>
                      <option value="Food & Beverage">Food & Beverage</option>
                      <option value="Manufacturing">Manufacturing</option>
                      <option value="Services">Services</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Description
                  </label>
                  <textarea
                    value={profileData.business_description}
                    onChange={(e) => setProfileData({...profileData, business_description: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500 bg-white"
                    placeholder="Describe your business"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Founded Year
                    </label>
                    <input
                      type="number"
                      value={profileData.founded_year}
                      onChange={(e) => setProfileData({...profileData, founded_year: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500 bg-white"
                      placeholder="2008"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Employee Count
                    </label>
                    <select
                      value={profileData.employee_count}
                      onChange={(e) => setProfileData({...profileData, employee_count: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500 bg-white"
                    >
                      <option value="">Select size</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="200+">200+ employees</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Website
                    </label>
                    <input
                      type="url"
                      value={profileData.website}
                      onChange={(e) => setProfileData({...profileData, website: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500 bg-white"
                      placeholder="https://yourbusiness.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Email
                    </label>
                    <input
                      type="email"
                      value={profileData.business_email}
                      onChange={(e) => setProfileData({...profileData, business_email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500 bg-white"
                      placeholder="contact@yourbusiness.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Phone
                    </label>
                    <input
                      type="tel"
                      value={profileData.business_phone}
                      onChange={(e) => setProfileData({...profileData, business_phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500 bg-white"
                      placeholder="+62..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Address
                  </label>
                  <textarea
                    value={profileData.address}
                    onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500 bg-white"
                    placeholder="Full business address"
                  />
                </div>

                {/* Specialties */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specialties
                  </label>
                  {profileData.specialties.map((specialty, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={specialty}
                        onChange={(e) => updateSpecialty(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500 bg-white"
                        placeholder={`Specialty ${index + 1}`}
                      />
                      {profileData.specialties.length > 1 && (
                        <button
                          onClick={() => removeSpecialty(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={addSpecialty}
                    className="text-teal-600 hover:text-teal-700 text-sm"
                  >
                    + Add Specialty
                  </button>
                </div>

                <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200 bg-white sticky bottom-0">
                  <button
                    onClick={() => setEditMode(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={updateProfile}
                    className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Profile;